import React, { useEffect, useMemo, useState } from 'react';
import {
    ClipboardCheck,
    ArrowRight,
    Check,
    X,
    Loader2,
    CalendarDays,
    AlertTriangle,
    Search,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { message } from 'antd';

const PendingApprovalWidget = ({ pendingApprovalsCount, pendingApprovalsPreviewItems, isEditMode, onActionComplete }) => {
    const { firmCode } = useAuth();
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;
    const [rejectionModal, setRejectionModal] = useState({ isOpen: false, itemId: null, reason: '' });

    const allItems = useMemo(() => pendingApprovalsPreviewItems || [], [pendingApprovalsPreviewItems]);

    const filteredItems = useMemo(() => {
        const q = searchQuery.trim().toLocaleLowerCase('tr-TR');
        if (!q) return allItems;
        return allItems.filter(item => {
            const text = [
                item.employeeFullName,
                item.leaveTypeName,
                item.type,
                item.dateRangeText,
                item.waitingForLabel
            ].filter(Boolean).join(' ').toLocaleLowerCase('tr-TR');
            return text.includes(q);
        });
    }, [allItems, searchQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * itemsPerPage;
    const paginatedItems = useMemo(
        () => filteredItems.slice(startIndex, startIndex + itemsPerPage),
        [filteredItems, startIndex]
    );

    useEffect(() => {
        if (isModalOpen) {
            setSearchQuery('');
            setCurrentPage(1);
        }
    }, [isModalOpen]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const openRejectionModal = (itemId) => {
        setRejectionModal({ isOpen: true, itemId, reason: '' });
    };

    const closeRejectionModal = () => {
        setRejectionModal({ isOpen: false, itemId: null, reason: '' });
    };

    const confirmRejection = async () => {
        const { itemId, reason } = rejectionModal;
        if (reason.trim().length < 10) return;

        try {
            setActionLoadingId(itemId);
            closeRejectionModal();

            await apiClient(`izin/${itemId}/reddet`, {
                method: 'PUT',
                body: JSON.stringify({ aciklama: reason.trim() })
            });

            message.success('İzin talebi reddedildi.');
            if (onActionComplete) {
                onActionComplete(true);
            }
        } catch (error) {
            console.error('İzin reddetme işlemi başarısız:', error);
            message.error(`İşlem sırasında bir hata oluştu: ${error.message || 'Bilinmeyen hata'}`);
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleAction = async (id, actionType) => {
        if (!firmCode) return;

        if (actionType === 'reject') {
            openRejectionModal(id);
            return;
        }

        try {
            setActionLoadingId(id);
            await apiClient(`izin/${id}/onayla`, {
                method: 'PUT',
                body: JSON.stringify({})
            });

            message.success('İzin talebi onaylandı.');
            if (onActionComplete) {
                onActionComplete(true);
            }
        } catch (error) {
            console.error(`İzin ${actionType} işlemi başarısız:`, error);
            message.error(`İşlem sırasında bir hata oluştu: ${error.message || 'Bilinmeyen hata'}`);
        } finally {
            setActionLoadingId(null);
        }
    };

    if (pendingApprovalsCount === undefined || pendingApprovalsCount === null) {
        return null;
    }

    return (
        <>
            <motion.div
                layout={!isEditMode}
                initial={isEditMode ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={isEditMode ? undefined : { opacity: 0, scale: 0.95 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-amber-200/80 dark:border-amber-900/50 relative group h-full flex flex-col overflow-hidden"
            >
                <div className="p-3.5 border-b border-amber-100 dark:border-amber-900/30 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 flex justify-between items-center shrink-0">
                    <div className="flex items-center space-x-2.5">
                        <div className="p-2 bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400 rounded-lg">
                            <ClipboardCheck size={18} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Onay Bekleyenler</h3>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">Aksiyon almanız gereken talepler</p>
                        </div>
                    </div>
                    {pendingApprovalsCount > 0 ? (
                        <button
                            onClick={() => !isEditMode && setIsModalOpen(true)}
                            className="flex flex-col items-end hover:bg-amber-100/50 dark:hover:bg-amber-900/40 px-2.5 py-1 rounded-lg transition-colors cursor-pointer text-left"
                            title="Tümünü görüntüle ve onayla"
                        >
                            <span className="text-2xl font-black text-amber-600 dark:text-amber-400 leading-none">
                                {pendingApprovalsCount}
                            </span>
                            <span className="text-[10px] font-medium text-amber-600/80 dark:text-amber-400/80 underline decoration-amber-600/30 underline-offset-2 mt-0.5">
                                Talebiniz var
                            </span>
                        </button>
                    ) : (
                        <span className="px-2.5 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-semibold rounded-full flex items-center">
                            <Check size={13} className="mr-1" />
                            Tümü Onaylandı
                        </span>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto">
                    {pendingApprovalsCount === 0 ? (
                        <div className="p-6 h-full text-center flex flex-col items-center justify-center space-y-2">
                            <div className="w-12 h-12 bg-gray-50 dark:bg-gray-700/50 rounded-full flex items-center justify-center">
                                <ClipboardCheck className="text-gray-400 dark:text-gray-500" size={24} />
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                Şu an onay bekleyen işlem bulunmuyor.
                            </p>
                        </div>
                    ) : (
                        <>
                            <ul className="divide-y divide-gray-100 dark:divide-gray-700/50">
                                {allItems.slice(0, 3).map((item) => (
                                    <li key={item.id} className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                        <div className="flex justify-between items-center">
                                            <div className="flex-1 min-w-0 pr-3">
                                                <div className="flex items-center space-x-2 mb-0.5">
                                                    <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                                        {item.type === 'Leave' ? (item.leaveTypeName || 'İzin Talebi') : item.type}
                                                    </span>
                                                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400 truncate">
                                                        {item.dateRangeText}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between mt-0.5">
                                                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate pr-2">
                                                        {item.employeeFullName}
                                                    </p>
                                                    <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                                        Talep: {new Date(item.requestedAt).toLocaleDateString('tr-TR')}
                                                    </span>
                                                </div>
                                            </div>
                                            {item.isActionableByMe && !isEditMode && (
                                                <div className="flex items-center space-x-1 pl-2">
                                                    <button
                                                        onClick={() => handleAction(item.id, 'approve')}
                                                        disabled={actionLoadingId === item.id}
                                                        title="Onayla"
                                                        className="p-1.5 text-green-600 hover:bg-green-100 dark:text-green-500 dark:hover:bg-green-900/30 rounded-lg transition-colors disabled:opacity-50"
                                                    >
                                                        {actionLoadingId === item.id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                                                    </button>
                                                    <button
                                                        onClick={() => openRejectionModal(item.id)}
                                                        disabled={actionLoadingId === item.id}
                                                        title="Reddet"
                                                        className="p-1.5 text-red-600 hover:bg-red-100 dark:text-red-500 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                                                    >
                                                        {actionLoadingId === item.id ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                                                    </button>
                                                </div>
                                            )}
                                            {item.waitingForLabel && !item.isActionableByMe && (
                                                <div className="text-[11px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-md">
                                                    {item.waitingForLabel}
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {allItems.length > 3 && (
                                <div className="p-2.5 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center border-t border-gray-100 dark:border-gray-700/50">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        +{allItems.length - 3} adet daha var
                                    </span>
                                    <button
                                        onClick={() => !isEditMode && setIsModalOpen(true)}
                                        className="text-xs font-semibold text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 inline-flex items-center cursor-pointer"
                                    >
                                        Tümünü Gör ({pendingApprovalsCount}) <ArrowRight size={14} className="ml-1" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </motion.div>

            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                        onClick={() => setIsModalOpen(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="shrink-0 flex justify-between items-center p-5 border-b border-gray-100 dark:border-gray-700/50 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400 rounded-lg">
                                        <ClipboardCheck size={20} />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                                            Onay Bekleyen İşlemler ({pendingApprovalsCount})
                                        </h2>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {filteredItems.length === allItems.length
                                                ? `${allItems.length} talep listeleniyor`
                                                : `${filteredItems.length} / ${allItems.length} talep`}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 bg-white/50 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-700 rounded-full transition-all"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Arama Barı */}
                            <div className="shrink-0 px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                                <label className="relative block">
                                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        type="search"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Personel, izin tipi veya tarih ara…"
                                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
                                    />
                                </label>
                            </div>

                            <div className="flex-1 min-h-0 overflow-y-auto p-2">
                                {filteredItems.length === 0 ? (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                                        Arama kriterlerine uygun talep bulunamadı.
                                    </p>
                                ) : (
                                    <ul className="divide-y divide-gray-100 dark:divide-gray-700/50">
                                        {paginatedItems.map((item) => (
                                            <li key={item.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors rounded-xl mx-2 my-1 border border-transparent hover:border-gray-100 dark:hover:border-gray-700/50">
                                                <div className="flex justify-between items-center">
                                                    <div className="flex-1 min-w-0 pr-4">
                                                        <div className="flex items-center space-x-2 mb-1">
                                                            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                                                {item.type === 'Leave' ? (item.leaveTypeName || 'İzin Talebi') : item.type}
                                                            </span>
                                                            <span className="text-sm font-medium text-amber-600 dark:text-amber-400 flex items-center">
                                                                <CalendarDays size={14} className="mr-1.5 opacity-70" />
                                                                {item.dateRangeText}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-baseline space-x-3 mt-0.5">
                                                            <p className="text-base font-bold text-gray-800 dark:text-gray-200">
                                                                {item.employeeFullName}
                                                            </p>
                                                            <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                                                                Talep: {new Date(item.requestedAt).toLocaleDateString('tr-TR')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {item.isActionableByMe ? (
                                                        <div className="flex items-center space-x-2 pl-2">
                                                            <button
                                                                onClick={() => handleAction(item.id, 'approve')}
                                                                disabled={actionLoadingId === item.id}
                                                                className="px-4 py-2 bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 rounded-xl transition-colors disabled:opacity-50 flex items-center font-semibold text-sm border border-green-200/50 dark:border-green-800/30 shadow-sm"
                                                            >
                                                                {actionLoadingId === item.id ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Check size={16} className="mr-1.5" />}
                                                                Onayla
                                                            </button>
                                                            <button
                                                                onClick={() => openRejectionModal(item.id)}
                                                                disabled={actionLoadingId === item.id}
                                                                className="px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 rounded-xl transition-colors disabled:opacity-50 flex items-center font-semibold text-sm border border-red-200/50 dark:border-red-800/30 shadow-sm"
                                                            >
                                                                {actionLoadingId === item.id ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <X size={16} className="mr-1.5" />}
                                                                Reddet
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700">
                                                            {item.waitingForLabel || 'Onay Bekliyor'}
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {filteredItems.length > itemsPerPage && (
                                <div className="shrink-0 flex justify-between items-center px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        Gösterilen: <strong className="text-gray-800 dark:text-gray-200">{startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredItems.length)}</strong> / {filteredItems.length}
                                    </span>
                                    <div className="flex items-center space-x-1.5">
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage(1)}
                                            disabled={safePage === 1}
                                            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
                                        >
                                            <ChevronsLeft size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={safePage === 1}
                                            className="px-3 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40 text-xs font-semibold inline-flex items-center gap-1"
                                        >
                                            <ChevronLeft size={14} /> Önceki
                                        </button>
                                        <span className="px-2 text-gray-700 dark:text-gray-300 text-xs font-semibold">
                                            Sayfa {safePage} / {totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={safePage === totalPages}
                                            className="px-3 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40 text-xs font-semibold inline-flex items-center gap-1"
                                        >
                                            Sonraki <ChevronRight size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={safePage === totalPages}
                                            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
                                        >
                                            <ChevronsRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Rejection Modal */}
            <AnimatePresence>
                {rejectionModal.isOpen && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-200 dark:border-gray-700"
                        >
                            <div className="p-5 border-b border-gray-100 dark:border-gray-700/50 flex items-center justify-between bg-red-50 dark:bg-red-900/10">
                                <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                                    <AlertTriangle size={20} />
                                    <h3 className="font-bold text-lg">İzni Reddet</h3>
                                </div>
                                <button onClick={closeRejectionModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="p-5 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reddetme Nedeni (Zorunlu)</label>
                                    <textarea
                                        autoFocus
                                        rows={3}
                                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:focus:ring-red-400 outline-none transition-all placeholder-gray-400 dark:placeholder-gray-500"
                                        placeholder="Lütfen reddetme nedenini buraya yazın..."
                                        value={rejectionModal.reason}
                                        onChange={(e) => setRejectionModal({ ...rejectionModal, reason: e.target.value })}
                                    />
                                    {rejectionModal.reason.trim().length > 0 && rejectionModal.reason.trim().length < 10 && (
                                        <p className="mt-1.5 text-xs text-red-500 dark:text-red-400 font-medium">
                                            Lütfen en az 10 karakterlik bir açıklama girin. (Kalan: {Math.max(0, 10 - rejectionModal.reason.trim().length)})
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/80 flex justify-end space-x-3 border-t border-gray-100 dark:border-gray-700/50">
                                <button
                                    onClick={closeRejectionModal}
                                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
                                >
                                    Vazgeç
                                </button>
                                <button
                                    onClick={confirmRejection}
                                    disabled={rejectionModal.reason.trim().length < 10 || actionLoadingId !== null}
                                    className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                >
                                    {actionLoadingId === rejectionModal.itemId && <Loader2 size={16} className="animate-spin mr-2" />}
                                    Talebi Reddet
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
};

export default PendingApprovalWidget;
